import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../../components'
import { normalizeTime } from '../../../../utils/receivingConfig'
import { validateFranja, overlappingFranja } from './franjaValidation'

const T = 'receiving_config.franjas.modal'
const inputClasses = 'shadow-sm block w-full sm:text-sm rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0] // Monday-first display, API values 0=Sunday

export const FranjaFormModal = ({ open, setOpen, initial, otherActiveFranjas = [], onSubmit }) => {
  const { t } = useTranslation()
  const labelRef = useRef(null)
  const [label, setLabel] = useState('')
  const [weekdays, setWeekdays] = useState([])
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setLabel(initial?.label || '')
    setWeekdays(initial?.weekdays ? [...initial.weekdays].map(Number) : [])
    setStartsAt(normalizeTime(initial?.starts_at) || '')
    setEndsAt(normalizeTime(initial?.ends_at) || '')
    setTouched(false)
  }, [open, initial])

  const values = { label, weekdays, starts_at: startsAt, ends_at: endsAt }
  const errors = validateFranja(values)
  const isValid = Object.keys(errors).length === 0
  const overlap = useMemo(
    () => (isValid ? overlappingFranja(values, otherActiveFranjas) : null),
    [isValid, label, weekdays, startsAt, endsAt, otherActiveFranjas], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const names = t('receiving_config.franjas.weekdays_short', { returnObjects: true })
  const fullNames = t('receiving_config.franjas.weekdays_full', { returnObjects: true })

  const toggleDay = d => setWeekdays(prev => (prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort((a, b) => a - b)))
  const close = () => setOpen(false)
  const submit = e => {
    e.preventDefault()
    setTouched(true)
    if (!isValid) return
    onSubmit({ ...(initial || {}), label: label.trim(), weekdays: [...weekdays].sort((a, b) => a - b), starts_at: startsAt, ends_at: endsAt })
    close()
  }
  const showError = field => (touched || values[field]) && errors[field]

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={labelRef} onClose={close}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                  {t(initial ? `${T}.title_edit` : `${T}.title_new`)}
                </Dialog.Title>
                <button type="button" className="text-gray-400 hover:text-gray-500" onClick={close} aria-label={t(`${T}.cancel`)}>
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="franja-label">
                    {t(`${T}.label`)}
                  </label>
                  <input
                    id="franja-label"
                    ref={labelRef}
                    type="text"
                    maxLength={100}
                    className={inputClasses}
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                  />
                  {showError('label') && <p className="text-red-500 text-xs mt-1">{t(`${T}.errors.${errors.label}`)}</p>}
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">{t(`${T}.weekdays`)}</span>
                  <div className="flex gap-1">
                    {WEEKDAYS.map(d => (
                      <button
                        key={d}
                        type="button"
                        aria-pressed={weekdays.includes(d)}
                        aria-label={fullNames[d]}
                        onClick={() => toggleDay(d)}
                        className={`w-9 h-9 rounded-full text-sm font-medium border ${weekdays.includes(d) ? 'bg-yellow-500 text-white border-yellow-500' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                      >
                        {names[d]}
                      </button>
                    ))}
                  </div>
                  {touched && errors.weekdays && <p className="text-red-500 text-xs mt-1">{t(`${T}.errors.${errors.weekdays}`)}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="franja-starts-at">
                      {t(`${T}.starts_at`)}
                    </label>
                    <input id="franja-starts-at" type="time" step="60" className={inputClasses} value={startsAt} onChange={e => setStartsAt(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="franja-ends-at">
                      {t(`${T}.ends_at`)}
                    </label>
                    <input id="franja-ends-at" type="time" step="60" className={inputClasses} value={endsAt} onChange={e => setEndsAt(e.target.value)} />
                  </div>
                </div>
                {startsAt && endsAt && errors.ends_at && <p className="text-red-500 text-xs">{t(`${T}.errors.${errors.ends_at}`)}</p>}
                {overlap && (
                  <p className="text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 text-xs">
                    {t(`${T}.errors.overlap_warning`, { label: overlap.label })}
                  </p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="secondary" onClick={close} className="px-4">
                    {t(`${T}.cancel`)}
                  </Button>
                  <Button type="primary" buttonType="submit" disabled={!isValid} className="px-4">
                    {t(`${T}.save`)}
                  </Button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
